import { useEffect, useMemo, useRef } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useContentToken, useCollectionBinding, useSlotGroup, useGallery, RichTextContent } from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { GlowTrace } from './GlowTrace';
import { formatBedroomsList } from '../utils/formatBedroomsList';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  label: string;
  subtitle: string;
  param: string;
  count: number;
  priceRange: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bedroom',
    two: '2 Bedroom',
    three: '3 Bedroom',
    four: '4 Bedroom',
    five: '5 Bedroom',
  };
  return labels[key] || `${key} Bedroom`;
}

function keyToShortLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bed',
    two: '2 Bed',
    three: '3 Bed',
    four: '4 Bed',
    five: '5 Bed',
  };
  return labels[key] || `${key} Bed`;
}

/**
 * Formats a raw bedrooms range into natural copy with every count listed,
 * mirroring the Hero subtitle: "1 - 3" -> "1, 2 & 3 Bedroom Layouts",
 * "studio - 2" -> "Studio, 1 & 2 Bedroom Layouts", "studio" -> "Studio Layouts".
 */
function formatBedroomsLayoutsLabel(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const label = formatBedroomsList(raw);
  if (!label) return null;
  return label === 'Studio' ? `${label} Layouts` : `${label} Bedroom Layouts`;
}

function formatPriceRange(plans: Floorplan[]): string {
  const prices = plans
    .flatMap((p) => [p.floorplan_price_min, p.floorplan_price_max])
    .map((v) => (v ? parseInt(String(v), 10) : NaN))
    .filter((v) => !isNaN(v) && v > 0);
  if (!prices.length) return 'Call for Pricing';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return `From $${min.toLocaleString()}`;
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => {
    const plans = groups[key];
    const count = plans.reduce((sum, p) => {
      const units = p.floorplan_available_units ? parseInt(String(p.floorplan_available_units), 10) : 0;
      return sum + (isNaN(units) ? 0 : units);
    }, 0);
    return {
      key,
      label: keyToLabel(key),
      subtitle: keyToShortLabel(key),
      param: key,
      count,
      priceRange: formatPriceRange(plans),
    };
  });
}

// ============================================================================
// Interior photo selection from gallery
// ============================================================================

const INTERIOR_KEYWORDS = [
  'interior', 'apartment', 'unit', 'bedroom', 'living room', 'living',
  'kitchen', 'bathroom', 'bath', 'inside', 'home', 'den', 'dining',
  'closet', 'counter', 'cabinet', 'window', 'floor', 'carpet',
  'hardwood', 'granite', 'stainless', 'appliance', 'washer', 'dryer',
];

function scoreInterior(photo: PhotoGallery): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  return INTERIOR_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
}

/**
 * Strips query params and trailing slashes so CDN variants of
 * the same image (e.g. ?width=400 vs ?width=800) are treated as one.
 */
function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

function pickInteriorPhotos(gallery: PhotoGallery[], count: number): string[] {
  if (!gallery.length) return [];

  // Deduplicate the gallery by normalized URL first
  const seen = new Set<string>();
  const unique: { photo: PhotoGallery; score: number }[] = [];
  for (const photo of gallery) {
    if (!photo.image) continue;
    const key = normalizeUrl(photo.image);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push({ photo, score: scoreInterior(photo) });
  }

  // Sort by score descending
  unique.sort((a, b) => b.score - a.score);

  // Take the top-scored unique photos, then spread them out by
  // distributing evenly across the gallery order to avoid visually
  // similar adjacent photos (e.g. 3 kitchen shots in a row).
  const pool = unique.slice(0, Math.max(count * 2, 8));
  if (pool.length <= count) {
    return pool.map((p) => p.photo.image!);
  }

  // Pick every Nth item to space them out visually
  const step = pool.length / count;
  const picked: string[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.min(Math.floor(i * step), pool.length - 1);
    picked.push(pool[idx].photo.image!);
  }
  return picked;
}

// ============================================================================
// Card slot group definitions
// ============================================================================
// Each floor plan card has an editable image, heading, and sub_label slot so
// users can override the auto-selected gallery photo and overlay text via the
// content editor. We support up to 7 cards (1 "View All" + 6 bedroom categories).

function makeCardDef(
  slotLabel: string,
  headingDefault: string,
  subLabelDefault: string,
): SlotGroupDefinition {
  return {
    label: slotLabel,
    fields: {
      image: {
        label: 'Card Image',
        type: 'image',
        defaultValue: '',
      },
      heading: {
        label: 'Card Heading',
        type: 'richtext',
        defaultValue: headingDefault,
      },
      sub_label: {
        label: 'Card Sub Label',
        type: 'richtext',
        defaultValue: subLabelDefault,
      },
    },
  };
}

const cardDefs = {
  viewAll: makeCardDef('View All Card', '<p>ALL</p>', '<p>View All Layouts</p>'),
  card0: makeCardDef('Card 1', '', ''),
  card1: makeCardDef('Card 2', '', ''),
  card2: makeCardDef('Card 3', '', ''),
  card3: makeCardDef('Card 4', '', ''),
  card4: makeCardDef('Card 5', '', ''),
  card5: makeCardDef('Card 6', '', ''),
};

const singleCtaDef: SlotGroupDefinition = {
  label: 'Single Floor Plan CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'View Floor Plan' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const bgPatternDef: SlotGroupDefinition = {
  label: 'Background Pattern',
  description:
    'Decorative tiled pattern behind the section with a parallax scroll and a color overlay. Hidden from screen readers, so no alt text is needed. Leave the image empty for a solid background.',
  fields: {
    image: {
      label: 'Pattern Image',
      type: 'image',
      defaultValue: '',
      description: 'A seamless, tileable image (PNG or SVG works best). Decorative only — no alt text needed.',
    },
    tile_size: {
      label: 'Tile Size (px)',
      type: 'text',
      defaultValue: '320',
      description: 'Width of one pattern tile in pixels (40–1200).',
    },
    overlay_color: {
      label: 'Overlay Color',
      type: 'text',
      defaultValue: 'primary',
      description:
        'A theme color name (primary, secondary, accent, background, surface, text) or any CSS color, e.g. #1a2b3c.',
    },
    overlay_opacity: {
      label: 'Overlay Opacity (%)',
      type: 'text',
      defaultValue: '85',
      description: '0 shows the pattern at full strength; 100 hides it completely.',
    },
  },
};

const THEME_OVERLAY_COLORS: Record<string, string> = {
  primary: 'var(--vibe-color-primary)',
  secondary: 'var(--vibe-color-secondary)',
  accent: 'var(--vibe-color-accent)',
  background: 'var(--vibe-color-background)',
  surface: 'var(--vibe-color-surface)',
  text: 'var(--vibe-color-text)',
};

function resolveOverlayColor(value: string | null | undefined): string {
  const trimmed = (value || '').trim();
  if (!trimmed) return THEME_OVERLAY_COLORS.primary;
  return THEME_OVERLAY_COLORS[trimmed.toLowerCase()] || trimmed;
}

function parseNumber(value: string | null | undefined, fallback: number, min: number, max: number): number {
  const parsed = parseFloat((value || '').replace('%', ''));
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(min, Math.min(max, parsed));
}

// ============================================================================
// Category Card Inner
// ============================================================================

function CategoryCardInner({
  cat,
  image,
  delay,
  headingHtml,
  subLabelHtml,
  aspectClass,
}: {
  cat: BedroomCategory;
  image: string;
  delay: number;
  headingHtml?: string | null;
  subLabelHtml?: string | null;
  aspectClass: string;
}) {
  const computedHeading = cat.key === 'studio' ? 'S' : BED_ORDER.indexOf(cat.key).toString();
  const computedSubLabel = `${cat.label} Layouts`;

  return (
    <a
      data-reveal
      href={`/floor-plans?bedroom=${cat.param}`}
      className={`reveal-fade-up fp-card glow-trace-wrap group relative block ${aspectClass} overflow-hidden cursor-pointer`}
    >
      <GlowTrace delay={delay} />
      {image && (
        <VibesitesImage
          url={image}
          alt={`${cat.label} floor plans`}
          className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
        />
      )}
      <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-[var(--vibe-color-text-on-primary)] px-4">
        {headingHtml ? (
          <div
            className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg"
            dangerouslySetInnerHTML={{ __html: headingHtml }}
          />
        ) : (
          <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">
            {computedHeading}
          </span>
        )}
        {subLabelHtml ? (
          <div
            className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md"
            dangerouslySetInnerHTML={{ __html: subLabelHtml }}
          />
        ) : (
          <span className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md">
            {computedSubLabel}
          </span>
        )}
      </div>
    </a>
  );
}

const CARD_ASPECT_DEFAULT = 'aspect-[4/3] sm:aspect-[3/4]';
const CARD_ASPECT_COMPACT = 'aspect-[4/3] sm:aspect-square';

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const label = useContentToken('floorplanslist.label');

  const formattedLabel = formatBedroomsLayoutsLabel(label);

  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const gallery = useGallery();

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  // +1 for the "View All" card
  const photos = useMemo(
    () => pickInteriorPhotos(gallery, categories.length + 1),
    [gallery, categories.length],
  );

  // Resolve card slot groups (hooks must be called unconditionally)
  const viewAllSlot = useSlotGroup('floorplanslist.card_viewall', cardDefs.viewAll);
  const cardSlot0 = useSlotGroup('floorplanslist.card_0', cardDefs.card0);
  const cardSlot1 = useSlotGroup('floorplanslist.card_1', cardDefs.card1);
  const cardSlot2 = useSlotGroup('floorplanslist.card_2', cardDefs.card2);
  const cardSlot3 = useSlotGroup('floorplanslist.card_3', cardDefs.card3);
  const cardSlot4 = useSlotGroup('floorplanslist.card_4', cardDefs.card4);
  const cardSlot5 = useSlotGroup('floorplanslist.card_5', cardDefs.card5);
  const cardSlots = [cardSlot0, cardSlot1, cardSlot2, cardSlot3, cardSlot4, cardSlot5];
  const singleCta = useSlotGroup('floorplanslist.single_cta', singleCtaDef);

  const bgPattern = useSlotGroup('floorplanslist.bg_pattern', bgPatternDef);
  const patternUrl = (bgPattern.image || '').trim();
  const tileSize = parseNumber(bgPattern.tile_size, 320, 40, 1200);
  const overlayColor = resolveOverlayColor(bgPattern.overlay_color);
  const overlayOpacity = parseNumber(bgPattern.overlay_opacity, 85, 0, 100) / 100;

  // Use overridden image if set, otherwise fall back to auto-picked gallery photo
  const viewAllImage = viewAllSlot?.image || photos[0] || '';
  const cardImages = categories.map((_, i) => cardSlots[i]?.image || photos[i + 1] || '');

  useRevealStyles();
  const headingRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });
  const ctaRef = useScrollReveal<HTMLDivElement>();
  const sectionRef = useRef<HTMLElement>(null);
  const patternRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const pattern = patternRef.current;
    if (!section || !pattern || !patternUrl) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let rafId = 0;
    let active = false;

    const tick = () => {
      const rect = section.getBoundingClientRect();
      const vh = window.innerHeight;
      const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh + rect.height)));
      pattern.style.transform = `translate3d(0, ${(progress - 0.5) * 120}px, 0)`;
      if (active) rafId = requestAnimationFrame(tick);
    };

    // IntersectionObserver (not window scroll) so the effect also runs inside
    // the editor iframe or any inner scroll container.
    const observer = new IntersectionObserver(
      ([entry]) => {
        active = entry.isIntersecting;
        cancelAnimationFrame(rafId);
        if (active) rafId = requestAnimationFrame(tick);
      },
      { threshold: 0, rootMargin: '200px' },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
      pattern.style.transform = '';
    };
  }, [patternUrl]);

  const hasCategories = categories.length > 0;
  // One bedroom category (or a single floor plan) leaves nothing meaningful to
  // pick among the cards — swap them for a direct CTA to /floor-plans.
  const isSingleOption = categories.length === 1;
  const totalCards = categories.length + 1;

  // Keep card widths near the 3-column size when there are fewer cards,
  // so 1–2 cards don't stretch across the full max-w-7xl container.
  // Fewer cards also use a shorter aspect ratio so they don't dominate the viewport.
  const gridLayout = useMemo(() => {
    if (totalCards <= 1) {
      return {
        cols: 'grid-cols-1',
        width: 'max-w-md mx-auto w-full',
        aspect: CARD_ASPECT_COMPACT,
      };
    }
    if (totalCards === 2) {
      return {
        cols: 'grid-cols-1 sm:grid-cols-2',
        width: 'max-w-md sm:max-w-3xl lg:max-w-4xl mx-auto w-full',
        aspect: CARD_ASPECT_COMPACT,
      };
    }
    if (totalCards === 3) {
      return {
        cols: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3',
        width: '',
        aspect: CARD_ASPECT_DEFAULT,
      };
    }
    if (totalCards === 4) {
      return {
        cols: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4',
        width: '',
        aspect: CARD_ASPECT_DEFAULT,
      };
    }
    if (totalCards === 5) {
      return {
        cols: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5',
        width: '',
        aspect: CARD_ASPECT_DEFAULT,
      };
    }
    return {
      cols: 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6',
      width: '',
      aspect: CARD_ASPECT_DEFAULT,
    };
  }, [totalCards]);

  return (
    <section ref={sectionRef} className="relative overflow-hidden py-16 md:py-24 bg-[var(--vibe-color-primary)]">
      {/* The pattern layer is 40% taller than the section so the ±60px
          parallax translate never exposes an edge. */}
      <div className="absolute inset-0 z-0" aria-hidden="true">
        <EditableSlotGroup
          groupPath="floorplanslist.bg_pattern"
          definition={bgPatternDef}
          className="relative h-full w-full overflow-hidden"
        >
          <div
            ref={patternRef}
            className="absolute left-0 right-0"
            style={{
              top: '-20%',
              bottom: '-20%',
              backgroundImage: patternUrl ? `url("${patternUrl}")` : undefined,
              backgroundRepeat: 'repeat',
              backgroundSize: `${tileSize}px auto`,
              willChange: patternUrl ? 'transform' : undefined,
            }}
          />
          <div
            className="absolute inset-0"
            style={{ backgroundColor: overlayColor, opacity: overlayOpacity }}
          />
        </EditableSlotGroup>
      </div>

      {/* pointer-events are disabled on the wrapper and re-enabled on
          interactive regions so clicks in the gutters reach the background
          pattern slot in the editor. */}
      <div className="relative z-10 pointer-events-none max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
        <div ref={headingRef} className="pointer-events-auto reveal-fade-up text-center mb-12">
          {formattedLabel && (
            <EditableToken path="floorplanslist.label" defaultField="property_bedrooms_range" as="p" className="font-body text-base sm:text-lg text-[var(--vibe-color-text-on-primary)] mb-3">
              {formattedLabel}
            </EditableToken>
          )}
          <RichTextContent path="floorplanslist.title" label="Title" defaultValue="<p>Make Yourself at Home</p>" as="h2" className="font-heading text-3xl sm:text-5xl font-bold text-[var(--vibe-color-text-on-primary)] mb-4 tracking-tight" />
          {isSingleOption ? (
            <RichTextContent path="floorplanslist.subtitle_single" label="Subtitle (Single Floor Plan)" defaultValue="<p>Explore our floor plan and find your perfect home.</p>" as="p" className="font-body text-base sm:text-lg text-[var(--vibe-color-text-on-primary)] max-w-2xl mx-auto" />
          ) : (
            <RichTextContent path="floorplanslist.subtitle" label="Subtitle" defaultValue="<p>Please select your desired layout below.</p>" as="p" className="font-body text-base sm:text-lg text-[var(--vibe-color-text-on-primary)] max-w-2xl mx-auto" />
          )}
        </div>

        {hasCategories && isSingleOption ? (
          <div ref={ctaRef} className="pointer-events-auto reveal-fade-up flex justify-center">
            <EditableSlotGroup groupPath="floorplanslist.single_cta" definition={singleCtaDef}>
              <a
                href={singleCta.url || '/floor-plans'}
                target={singleCta._target || '_self'}
                className="fp-single-cta inline-flex items-center gap-2"
              >
                <span>{singleCta.label || 'View Floor Plan'}</span>
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
                </svg>
              </a>
            </EditableSlotGroup>
          </div>
        ) : hasCategories ? (
          <div ref={gridRef} className={`pointer-events-auto grid ${gridLayout.cols} ${gridLayout.width} gap-3 md:gap-4`}>
            {/* "View All" card first — editable image + richtext heading/sub_label */}
            <EditableSlotGroup groupPath="floorplanslist.card_viewall" definition={cardDefs.viewAll}>
              <a
                data-reveal
                href="/floor-plans"
                className={`reveal-fade-up fp-card glow-trace-wrap group relative block ${gridLayout.aspect} overflow-hidden cursor-pointer`}
              >
                <GlowTrace delay={0.3} />
                {viewAllImage && (
                  <VibesitesImage
                    url={viewAllImage}
                    alt="View all floor plans"
                    className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                )}
                <div className="absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
                <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-[var(--vibe-color-text-on-primary)] px-4">
                  <div
                    className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg"
                    dangerouslySetInnerHTML={{ __html: viewAllSlot?.heading || '<p>ALL</p>' }}
                  />
                  <div
                    className="font-body text-[11px] sm:text-xs uppercase tracking-[0.2em] drop-shadow-md"
                    dangerouslySetInnerHTML={{ __html: viewAllSlot?.sub_label || '<p>View All Layouts</p>' }}
                  />
                </div>
              </a>
            </EditableSlotGroup>

            {/* Bedroom category cards — each with editable image + richtext overlay (explicit paths for linter) */}
            {categories[0] && <EditableSlotGroup groupPath="floorplanslist.card_0" definition={cardDefs.card0}><CategoryCardInner cat={categories[0]} image={cardImages[0]} delay={0.55} headingHtml={cardSlots[0]?.heading || null} subLabelHtml={cardSlots[0]?.sub_label || null} aspectClass={gridLayout.aspect} /></EditableSlotGroup>}
            {categories[1] && <EditableSlotGroup groupPath="floorplanslist.card_1" definition={cardDefs.card1}><CategoryCardInner cat={categories[1]} image={cardImages[1]} delay={0.80} headingHtml={cardSlots[1]?.heading || null} subLabelHtml={cardSlots[1]?.sub_label || null} aspectClass={gridLayout.aspect} /></EditableSlotGroup>}
            {categories[2] && <EditableSlotGroup groupPath="floorplanslist.card_2" definition={cardDefs.card2}><CategoryCardInner cat={categories[2]} image={cardImages[2]} delay={1.05} headingHtml={cardSlots[2]?.heading || null} subLabelHtml={cardSlots[2]?.sub_label || null} aspectClass={gridLayout.aspect} /></EditableSlotGroup>}
            {categories[3] && <EditableSlotGroup groupPath="floorplanslist.card_3" definition={cardDefs.card3}><CategoryCardInner cat={categories[3]} image={cardImages[3]} delay={1.30} headingHtml={cardSlots[3]?.heading || null} subLabelHtml={cardSlots[3]?.sub_label || null} aspectClass={gridLayout.aspect} /></EditableSlotGroup>}
            {categories[4] && <EditableSlotGroup groupPath="floorplanslist.card_4" definition={cardDefs.card4}><CategoryCardInner cat={categories[4]} image={cardImages[4]} delay={1.55} headingHtml={cardSlots[4]?.heading || null} subLabelHtml={cardSlots[4]?.sub_label || null} aspectClass={gridLayout.aspect} /></EditableSlotGroup>}
            {categories[5] && <EditableSlotGroup groupPath="floorplanslist.card_5" definition={cardDefs.card5}><CategoryCardInner cat={categories[5]} image={cardImages[5]} delay={1.80} headingHtml={cardSlots[5]?.heading || null} subLabelHtml={cardSlots[5]?.sub_label || null} aspectClass={gridLayout.aspect} /></EditableSlotGroup>}
          </div>
        ) : (
          <div className="text-center py-12 text-[var(--vibe-color-text-on-primary)]/60">
            <p className="font-body text-lg">Floor plans will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .fp-card {
          border-radius: 4px;
        }
        .fp-card-overlay {
          background: linear-gradient(
            180deg,
            color-mix(in srgb, var(--vibe-color-primary) 70%, black) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 85%, black) 100%
          );
          opacity: 0.3;
        }
        .fp-card:hover .fp-card-overlay {
          opacity: 0.45;
        }
        .fp-single-cta {
          padding: 0.875rem 2rem;
          font-family: var(--vibe-font-body);
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          text-decoration: none;
          color: var(--vibe-color-text-on-secondary);
          background: linear-gradient(135deg, var(--vibe-color-secondary) 0%, color-mix(in srgb, var(--vibe-color-secondary) 80%, black) 100%);
          border-radius: 9999px;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .fp-single-cta:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 35%, transparent);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.label': {
      label: 'Section Label',
      type: 'text',
      defaultField: 'property_bedrooms_range',
      description: 'Label above the title showing bedroom range (e.g., "1, 2 & 3 Bedroom Layouts").',
      suggestedFields: ['property_bedrooms_range'],
    },
    'floorplanslist.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Make Yourself at Home</p>',
    },
    'floorplanslist.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Please select your desired layout below.</p>',
    },
    'floorplanslist.subtitle_single': {
      label: 'Subtitle (Single Floor Plan)',
      type: 'richtext',
      defaultValue: '<p>Explore our floor plan and find your perfect home.</p>',
      description: 'Shown instead of the default subtitle when only one bedroom category is available.',
    },
  },
  slotGroups: {
    'floorplanslist.single_cta': {
      label: 'Single Floor Plan CTA',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
        label: { label: 'Button Text', type: 'text', defaultValue: 'View Floor Plan' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'floorplanslist.card_viewall': {
      label: 'View All Card',
      fields: {
        image: { label: 'Card Image', type: 'image', defaultValue: '' },
        heading: { label: 'Card Heading', type: 'richtext', defaultValue: '<p>ALL</p>' },
        sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '<p>View All Layouts</p>' },
      },
    },
    'floorplanslist.card_0': {
      label: 'Card 1',
      fields: {
        image: { label: 'Card Image', type: 'image', defaultValue: '' },
        heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
        sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
      },
    },
    'floorplanslist.card_1': {
      label: 'Card 2',
      fields: {
        image: { label: 'Card Image', type: 'image', defaultValue: '' },
        heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
        sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
      },
    },
    'floorplanslist.card_2': {
      label: 'Card 3',
      fields: {
        image: { label: 'Card Image', type: 'image', defaultValue: '' },
        heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
        sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
      },
    },
    'floorplanslist.card_3': {
      label: 'Card 4',
      fields: {
        image: { label: 'Card Image', type: 'image', defaultValue: '' },
        heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
        sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
      },
    },
    'floorplanslist.card_4': {
      label: 'Card 5',
      fields: {
        image: { label: 'Card Image', type: 'image', defaultValue: '' },
        heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
        sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
      },
    },
    'floorplanslist.card_5': {
      label: 'Card 6',
      fields: {
        image: { label: 'Card Image', type: 'image', defaultValue: '' },
        heading: { label: 'Card Heading', type: 'richtext', defaultValue: '' },
        sub_label: { label: 'Card Sub Label', type: 'richtext', defaultValue: '' },
      },
    },
    'floorplanslist.bg_pattern': {
      label: 'Background Pattern',
      description:
        'Decorative tiled pattern behind the section with a parallax scroll and a color overlay. Hidden from screen readers, so no alt text is needed. Leave the image empty for a solid background.',
      fields: {
        image: {
          label: 'Pattern Image',
          type: 'image',
          defaultValue: '',
          description: 'A seamless, tileable image (PNG or SVG works best). Decorative only — no alt text needed.',
        },
        tile_size: {
          label: 'Tile Size (px)',
          type: 'text',
          defaultValue: '320',
          description: 'Width of one pattern tile in pixels (40–1200).',
        },
        overlay_color: {
          label: 'Overlay Color',
          type: 'text',
          defaultValue: 'primary',
          description:
            'A theme color name (primary, secondary, accent, background, surface, text) or any CSS color, e.g. #1a2b3c.',
        },
        overlay_opacity: {
          label: 'Overlay Opacity (%)',
          type: 'text',
          defaultValue: '85',
          description: '0 shows the pattern at full strength; 100 hides it completely.',
        },
      },
    },
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
